import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { getPackageById, relocationPackages } from '../data/relocation';
import SEO from '../components/SEO';

const RelocationIntake = () => {
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const preselectedPackage = searchParams.get('package') || '';

  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [leadId, setLeadId] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    packageId: preselectedPackage || 'digital',
    timeline: '',
    householdSize: '',
    currentLocation: '',
    notes: ''
  });

  const selectedPackage = getPackageById(formData.packageId);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleIntakeSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/relocation/intake`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || null,
          package_id: formData.packageId,
          timeline: formData.timeline || null,
          household_size: formData.householdSize || null,
          current_location: formData.currentLocation || null,
          notes: formData.notes || null
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to submit intake');
      }

      const data = await response.json();
      setLeadId(data.lead_id);
      setStep(2);
    } catch (err) {
      setError(err.message || 'Unable to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProceedToCheckout = async () => {
    if (!leadId) return;
    
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/relocation/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lead_id: leadId,
          origin_url: window.location.origin
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to create checkout session');
      }

      const data = await response.json();
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(err.message || 'Unable to proceed to payment. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F6EF]">
      <SEO
        title="Relocation Intake"
        description="Start your relocation journey to Mexico. Complete the intake form for personalized relocation packages from The Mundalo."
        path="/relocation/intake"
      />
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#F9F6EF] border-b border-[#F4ECD8]">
        <div className="container-wide mx-auto px-8 py-5">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center gap-3">
              <img src="https://res.cloudinary.com/des9fk8et/image/upload/e_trim,f_auto,q_auto:good/mundalo/brand/mundalo-logo-white.png" alt="Mundalo" className="block h-7 w-[140px] object-contain object-left" />
            </a>
            <nav className="hidden md:flex items-center gap-8">
              <a href="/relocation/cdmx" className="text-sm font-medium text-[#1A5C6E] hover:text-[#2CA8A0] transition">
                {t('intake.backToRelocation')}
              </a>
            </nav>
          </div>
        </div>
      </header>

      <section className="section">
        <div className="container-narrow mx-auto">
          {/* Progress indicator */}
          <div className="flex items-center justify-center gap-4 mb-12">
            <div className={`flex items-center gap-2 ${step >= 1 ? 'text-[#2CA8A0]' : 'text-[#5A6B73]'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold ${
                step >= 1 ? 'bg-[#2CA8A0] text-[#F9F6EF]' : 'bg-[#E0D6B8]'
              }`}>1</div>
              <span className="text-sm font-medium">{t('intake.step1')}</span>
            </div>
            <div className="w-12 h-px bg-[#E0D6B8]" />
            <div className={`flex items-center gap-2 ${step >= 2 ? 'text-[#2CA8A0]' : 'text-[#5A6B73]'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold ${
                step >= 2 ? 'bg-[#2CA8A0] text-[#F9F6EF]' : 'bg-[#E0D6B8]'
              }`}>2</div>
              <span className="text-sm font-medium">{t('intake.step2')}</span>
            </div>
            <div className="w-12 h-px bg-[#E0D6B8]" />
            <div className="flex items-center gap-2 text-[#5A6B73]">
              <div className="w-8 h-8 rounded-full flex items-center justify-center font-semibold bg-[#E0D6B8]">3</div>
              <span className="text-sm font-medium">{t('intake.step3')}</span>
            </div>
          </div>

          {step === 1 && (
            <>
              <div className="text-center mb-8">
                <h1 className="heading-lg mb-4">{t('intake.title')}</h1>
                <p className="body-md text-[#1A5C6E]">{t('intake.subtitle')}</p>
              </div>

              <form onSubmit={handleIntakeSubmit} className="space-y-6">
                {error && (
                  <div className="bg-red-900/20 border border-red-800 text-red-400 px-4 py-3 text-sm">
                    {error}
                  </div>
                )}

                {/* Package Selection */}
                <div>
                  <label className="block text-sm font-medium mb-3 text-[#0A1F2E]">{t('intake.selectPlan')}</label>
                  <div className="grid md:grid-cols-3 gap-4">
                    {relocationPackages.map((pkg) => (
                      <button
                        key={pkg.id}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, packageId: pkg.id }))}
                        className={`p-4 border text-left transition-colors ${
                          formData.packageId === pkg.id 
                            ? 'border-[#2CA8A0] bg-[#FFFFFF]' 
                            : 'border-[#E0D6B8] bg-[#FFFFFF] hover:border-[#C9BC95]'
                        }`}
                        data-testid={`package-select-${pkg.id}`}
                      >
                        <p className="font-semibold text-[#0A1F2E]">{pkg.name}</p>
                        <p className="text-lg font-bold text-[#2CA8A0] mt-1">{pkg.price}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Contact Info */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-[#0A1F2E]">{t('intake.name')} *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-[#E0D6B8] bg-[#FFFFFF] text-[#0A1F2E]"
                      data-testid="intake-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-[#0A1F2E]">{t('intake.email')} *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-[#E0D6B8] bg-[#FFFFFF] text-[#0A1F2E]"
                      data-testid="intake-email"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-[#0A1F2E]">{t('intake.phone')}</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-[#E0D6B8] bg-[#FFFFFF] text-[#0A1F2E]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-[#0A1F2E]">{t('intake.currentLocation')}</label>
                    <input
                      type="text"
                      name="currentLocation"
                      value={formData.currentLocation}
                      onChange={handleInputChange}
                      placeholder={t('intake.cityCountry')}
                      className="w-full px-4 py-3 border border-[#E0D6B8] bg-[#FFFFFF] text-[#0A1F2E]"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-[#0A1F2E]">{t('intake.timeline')}</label>
                    <select
                      name="timeline"
                      value={formData.timeline}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-[#E0D6B8] bg-[#FFFFFF] text-[#0A1F2E]"
                    >
                      <option value="">{t('intake.selectTimeline')}</option>
                      <option value="1-3-months">{t('intake.months13')}</option>
                      <option value="3-6-months">{t('intake.months36')}</option>
                      <option value="6-12-months">{t('intake.months612')}</option>
                      <option value="just-researching">{t('intake.justResearching')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-[#0A1F2E]">{t('intake.householdSize')}</label>
                    <select
                      name="householdSize"
                      value={formData.householdSize}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-[#E0D6B8] bg-[#FFFFFF] text-[#0A1F2E]"
                    >
                      <option value="">{t('intake.selectSize')}</option>
                      <option value="single">{t('intake.single')}</option>
                      <option value="couple">{t('intake.couple')}</option>
                      <option value="family-small">{t('intake.familySmall')}</option>
                      <option value="family-large">{t('intake.familyLarge')}</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-[#0A1F2E]">{t('intake.additionalNotes')}</label>
                  <textarea
                    name="notes"
                    rows="4"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder={t('intake.notesPlaceholder')}
                    className="w-full px-4 py-3 border border-[#E0D6B8] bg-[#FFFFFF] text-[#0A1F2E]"
                  />
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full disabled:opacity-50"
                    data-testid="intake-submit"
                  >
                    {isSubmitting ? t('intake.submitting') : t('intake.continueToConfirm')}
                  </button>
                </div>
              </form>
            </>
          )}

          {step === 2 && selectedPackage && (
            <>
              <div className="text-center mb-8">
                <h1 className="heading-lg mb-4">{t('intake.confirmTitle')}</h1>
                <p className="body-md text-[#1A5C6E]">{t('intake.confirmSubtitle')}</p>
              </div>

              {error && (
                <div className="bg-red-900/20 border border-red-800 text-red-400 px-4 py-3 text-sm mb-6">
                  {error}
                </div>
              )}

              {/* Summary Card */}
              <div className="bg-[#FFFFFF] border border-[#E0D6B8] p-8 mb-8">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <p className="label mb-1">{t('intake.selectedPlan')}</p>
                    <h2 className="heading-md">{selectedPackage.name}</h2>
                  </div>
                  <p className="text-3xl font-bold text-[#2CA8A0]">{selectedPackage.price}</p>
                </div>

                <div className="border-t border-[#E0D6B8] pt-6 mb-6">
                  <p className="text-sm font-medium text-[#0A1F2E] mb-4">{t('intake.whatsIncluded')}</p>
                  <ul className="space-y-2">
                    {selectedPackage.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Check className="w-4 h-4 text-[#2CA8A0] flex-shrink-0 mt-0.5" />
                        <span className="body-sm text-[#1A5C6E]">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="border-t border-[#E0D6B8] pt-6">
                  <p className="text-sm font-medium text-[#0A1F2E] mb-3">{t('intake.yourInfo')}</p>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-[#5A6B73]">{t('intake.name')}:</span>{' '}
                      <span className="text-[#0A1F2E]">{formData.name}</span>
                    </div>
                    <div>
                      <span className="text-[#5A6B73]">{t('intake.email')}:</span>{' '}
                      <span className="text-[#0A1F2E]">{formData.email}</span>
                    </div>
                    {formData.currentLocation && (
                      <div>
                        <span className="text-[#5A6B73]">{t('intake.currentLocation')}:</span>{' '}
                        <span className="text-[#0A1F2E]">{formData.currentLocation}</span>
                      </div>
                    )}
                    {formData.timeline && (
                      <div>
                        <span className="text-[#5A6B73]">{t('intake.timeline')}:</span>{' '}
                        <span className="text-[#0A1F2E]">{formData.timeline.replace(/-/g, ' ')}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <button
                  onClick={handleProceedToCheckout}
                  disabled={isSubmitting}
                  className="btn-primary w-full disabled:opacity-50"
                  data-testid="confirm-checkout"
                >
                  {isSubmitting ? t('intake.redirectingPayment') : t('intake.confirmSecure')}
                </button>
                
                <button
                  onClick={() => setStep(1)}
                  className="text-sm font-medium text-[#5A6B73] hover:text-[#2CA8A0] transition"
                >
                  {t('intake.backToEdit')}
                </button>
              </div>

              <p className="body-sm text-center text-[#5A6B73] mt-6">
                {t('intake.securePayment')}
              </p>
            </>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="section-sm border-t border-[#F4ECD8]">
        <div className="container-wide mx-auto text-center">
          <p className="body-sm text-[#5A6B73]">© {new Date().getFullYear()} Mundalo</p>
        </div>
      </footer>
    </div>
  );
};

export default RelocationIntake;
